/**
 * Built-in product gallery (the PDP image carousel).
 *
 * Shared by the storefront product page and the admin Store Editor, so an
 * admin can load the existing carousel images and replace them one by one.
 */
import liverBiome01 from "@/assets/liver-biome-v2/01-hero.webp.asset.json";
import liverBiome02 from "@/assets/liver-biome-v2/02-who-its-for.webp.asset.json";
import liverBiome03 from "@/assets/liver-biome-v2/03-actives-and-doses.webp.asset.json";
import liverBiome04 from "@/assets/liver-biome-v2/04-liver-gut-system.webp.asset.json";
import liverBiome05 from "@/assets/liver-biome-v2/05-delivery-system.webp.asset.json";
import liverBiome06 from "@/assets/liver-biome-v2/06-usage-and-safety.webp.asset.json";
import liverBiome08 from "@/assets/liver-biome-v2/08-active-functions.webp.asset.json";
import liverBiome09 from "@/assets/liver-biome-v2/09-probiotic-rationale.webp.asset.json";
import liverBiome10 from "@/assets/liver-biome-v2/10-daily-support-disclaimer.webp.asset.json";
import magAbsorbV2_01 from "@/assets/product-hero/mag-absorb-hero.png.asset.json";
import magAbsorbV2_02 from "@/assets/mag-absorb-pro-v2/02-who-its-for-2.webp.asset.json";
import magAbsorbV2_03 from "@/assets/mag-absorb-pro-v2/03-five-forms-and-doses.webp.asset.json";
import magAbsorbV2_04 from "@/assets/mag-absorb-pro-v2/04-cofactors-and-delivery.webp.asset.json";
import magAbsorbV2_05 from "@/assets/mag-absorb-pro-v2/05-comparison.webp.asset.json";
import magAbsorbV2_06 from "@/assets/mag-absorb-pro-v2/06-usage-and-safety-2.webp.asset.json";
import magAbsorbV2_08 from "@/assets/mag-absorb-pro-v2/08-why-aquamin.webp.asset.json";
import magAbsorbV2_09 from "@/assets/mag-absorb-pro-v2/09-complementary-roles.webp.asset.json";
import magAbsorbV2_10 from "@/assets/mag-absorb-pro-v2/10-what-to-expect.webp.asset.json";
import ovaBalance2 from "@/assets/ova-balance/02-system.webp";
import ovaBalance3 from "@/assets/ova-balance/03-ratio.webp";
import ovaBalance4 from "@/assets/ova-balance/04-ritual.webp";
import ovaBalance5 from "@/assets/ova-balance/05-feel.webp";
import ovaBalanceHeroAsset from "@/assets/product-hero/ova-balance-hero.png.asset.json";

export type GalleryImage = { src: string; alt: string };

export const LIVER_BIOME_GALLERY: GalleryImage[] = [
  { src: liverBiome01.url, alt: "Liver Biome Plus, liposomal liver + gut support" },
  { src: liverBiome02.url, alt: "Who Liver Biome Plus is for" },
  { src: liverBiome03.url, alt: "Clinically-studied actives and doses" },
  { src: liverBiome04.url, alt: "One formula for liver + gut" },
  { src: liverBiome05.url, alt: "Liposomal delivery system, why delivery matters" },
  { src: liverBiome06.url, alt: "How to take Liver Biome Plus safely" },
  { src: liverBiome08.url, alt: "What each active supports" },
  { src: liverBiome09.url, alt: "Why probiotics in a liver formula?" },
  { src: liverBiome10.url, alt: "Daily support, not a juice cleanse" },
];

export const MAG_ABSORB_GALLERY: GalleryImage[] = [
  { src: magAbsorbV2_01.url, alt: "Mag Absorb Pro. India's 1st 5-form magnesium complex" },
  { src: magAbsorbV2_02.url, alt: "Who Mag Absorb Pro is for" },
  { src: magAbsorbV2_03.url, alt: "5 complementary magnesium forms with doses" },
  {
    src: magAbsorbV2_04.url,
    alt: "Cofactors for utilisation, zinc, copper, B6, D3, Bio-Absorb Matrix",
  },
  { src: magAbsorbV2_05.url, alt: "Why this formula is different from standard magnesium" },
  { src: magAbsorbV2_06.url, alt: "How to take Mag Absorb Pro" },
  { src: magAbsorbV2_08.url, alt: "Why Aquamin® marine magnesium" },
  { src: magAbsorbV2_09.url, alt: "Five forms, complementary roles" },
  { src: magAbsorbV2_10.url, alt: "What to expect from daily use" },
];

export const OVA_BALANCE_GALLERY: GalleryImage[] = [
  { src: ovaBalanceHeroAsset.url, alt: "TruWe Ova Balance. Hormone Balance System" },
  { src: ovaBalance2, alt: "Complete PCOS support system" },
  { src: ovaBalance3, alt: "Powered by 40:1 gold standard ratio" },
  { src: ovaBalance4, alt: "How to enjoy Ova Balance" },
  { src: ovaBalance5, alt: "Balance hormones, support cycles, feel like you again" },
];

/** Built-in carousel images for a product handle, or null when not a core SKU. */
export function getBuiltInGalleryForHandle(handle: string): GalleryImage[] | null {
  const h = (handle ?? "").toLowerCase();
  if (h.includes("liver-biome")) return LIVER_BIOME_GALLERY;
  if (h.includes("mag-absorb")) return MAG_ABSORB_GALLERY;
  if (h.includes("ova")) return OVA_BALANCE_GALLERY;
  return null;
}
